import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { PermissionService } from '../../../permissions.service';
import { HeroReport } from '../../../model/hero-report/hero-report.model';
import {
  ReportExportFailedError,
  ReportExportService,
  ReportExportTimedOutError,
} from '../../../model/report-export/report-export.service';
import { downloadFile } from '../../../shared/download-file';
import { renderExportMapImage } from '../../../shared/export-map/export-map-image';
import {
  ExportReportType,
  EventFilterValues,
  MAX_EXPORT_REPORTS,
  NotExportableReason,
  apiErrorCode,
  buildExportRequest,
  checkExportable,
  exportFileName,
  exportPins,
  reportsToExport,
} from '../../../utilities/reportExport';

/**
 * "Export to: [SRE Report | EOD Report] [Export]" in the top bar of the events page. Builds the report of the chosen
 * type from the events shown and the filters set, and saves it as <region>-<organization>-<date>.pdf.
 */
@Component({
  selector: 'app-export-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DropdownModule],
  templateUrl: './export-reports.component.html',
  styleUrl: './export-reports.component.scss',
})
export class ExportReportsComponent {
  /** The events on the page after the filters and the pending/completed toggles. */
  @Input() events: HeroReport[] = [];

  /** The filters as set above the events. */
  @Input() filters: EventFilterValues | null = null;

  @Input() showPendingEvents = true;
  @Input() showCompletedEvents = true;

  reportTypeOptions: { label: string; value: ExportReportType }[] = [
    { label: $localize`:@@export.type.sre:SRE Report`, value: 'SRE' },
    { label: $localize`:@@export.type.eod:EOD Report`, value: 'EOD' },
  ];

  reportType: ExportReportType = 'SRE';
  isExporting = false;
  message: string | null = null;

  /** Only the roles the API lets build reports see the control. */
  readonly canExport: boolean;

  constructor(
    permissionService: PermissionService,
    private reportExportService: ReportExportService,
  ) {
    this.canExport = permissionService.can('export:reports');
  }

  async onExport(): Promise<void> {
    if (this.isExporting) {
      return;
    }

    this.message = null;

    const reports = reportsToExport(this.events, this.reportType);
    const check = checkExportable(reports);
    if (!check.ok) {
      this.message = this.messageForNotExportable(check.reason ?? 'none');
      return;
    }

    this.isExporting = true;
    try {
      const mapDataUrl = await renderExportMapImage(exportPins(reports));
      const request = buildExportRequest(this.filters, this.showPendingEvents, this.showCompletedEvents, mapDataUrl);

      const job = await this.reportExportService.start(check.organizationId, this.reportType, request);
      const done = await this.reportExportService.waitUntilDone(job.id);
      const pdf = await this.reportExportService.fetchPdf(job.id);

      // Saved, not opened: the name comes from the API (region, organization and the date it was generated).
      downloadFile(pdf, exportFileName(done.fileName));
    } catch (error) {
      console.error('Error exporting report:', error);
      this.message = this.messageForError(error);
    } finally {
      this.isExporting = false;
    }
  }

  private messageForNotExportable(reason: NotExportableReason): string {
    switch (reason) {
      case 'multiple-organizations':
        return $localize`:@@export.error.multipleOrganizations:These reports come from more than one organization. Narrow the filters to one organization to export.`;
      case 'too-many':
        return $localize`:@@export.error.tooMany:An export can list up to ${MAX_EXPORT_REPORTS}:max: reports. Narrow the filters to export fewer.`;
      default:
        return $localize`:@@export.error.none:There are no reports to export with these filters.`;
    }
  }

  private messageForError(error: unknown): string {
    if (error instanceof ReportExportTimedOutError) {
      return this.messageForCode('REPORT_EXPORT_TIMED_OUT_EXCEPTION');
    }
    if (error instanceof ReportExportFailedError) {
      return this.messageForCode(error.errorCode);
    }
    return this.messageForCode(apiErrorCode(error));
  }

  private messageForCode(errorCode: string | null): string {
    switch (errorCode) {
      case 'REPORT_EXPORT_NO_REPORTS_EXCEPTION':
        return this.messageForNotExportable('none');
      case 'REPORT_EXPORT_TOO_MANY_REPORTS_EXCEPTION':
        return this.messageForNotExportable('too-many');
      case 'REPORT_EXPORT_BUSY_EXCEPTION':
        return $localize`:@@export.error.busy:Too many reports are being built right now. Try again in a few minutes.`;
      case 'REPORT_EXPORT_TIMED_OUT_EXCEPTION':
        return $localize`:@@export.error.timedOut:The report took too long to build. Try again, or narrow the filters.`;
      default:
        return $localize`:@@export.error.generic:The report could not be built. Please try again.`;
    }
  }
}
